import { useMemo } from 'react';
import { CalendarDays, Clock, ExternalLink, GripVertical, MapPin, Navigation, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { format, isSameDay, isSameMonth } from 'date-fns';
import { mapsUrl } from '@/lib/formatters';
import { ResponsiveRecordCard, ResponsiveRecordList } from '@/components/ui/responsive-record-list';

export function JobsCalendarViews({ data, actions, helpers }) {
  const { calView, monthCalDays, monthDate, weekDays, dragOverDay, dragJobId, dayTimeSlots, dayJobs, jobsList, selected } = data;
  const { getJobsForDay, getDBAColor, getStaffName, statusColors } = helpers;
  const { goToWeekOf, goToDay, handleDragOver, handleDragLeave, handleDrop, handleDragStart, handleDragEnd, setShowDetail, toggleSelectAll, toggleSelect } = actions;
  const unscheduledDayJobs = useMemo(() => dayJobs.filter(job => !job.scheduled_time_start), [dayJobs]);
  return (
    <>
      {/* MONTH VIEW */}
      {calView === 'month' && (
        <div className="hidden overflow-hidden rounded-sm border border-zinc-200 md:block" data-testid="month-calendar">
          <div className="grid grid-cols-7 bg-zinc-50 border-b border-zinc-200">
            {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
              <div key={d} className="text-center py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px bg-zinc-200">
            {monthCalDays.map(day => {
              const isToday = isSameDay(day, new Date());
              const isThisMonth = isSameMonth(day, monthDate);
              const dayJobsList = getJobsForDay(day);
              return (
                <div key={day.toISOString()}
                  className={`bg-white min-h-[80px] p-1.5 cursor-pointer hover:bg-zinc-50 transition-colors ${!isThisMonth ? 'opacity-40' : ''}`}
                  onClick={() => goToWeekOf(day)}
                  data-testid={`month-day-${format(day, 'yyyy-MM-dd')}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium ${isToday ? 'flex h-6 w-6 items-center justify-center rounded-sm bg-zinc-950 text-white' : 'text-zinc-700'}`}>
                      {format(day, 'd')}
                    </span>
                    {dayJobsList.length > 0 && (
                      <span className="rounded-sm bg-zinc-900 px-1.5 py-0.5 text-[10px] font-bold text-white">{dayJobsList.length}</span>
                    )}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {dayJobsList.slice(0, 3).map(j => (
                      <div key={j.id} className="text-[10px] truncate px-1 py-0.5 rounded" style={{ borderLeft: `2px solid ${getDBAColor(j.dba_id)}` }}>
                        {j.title}
                      </div>
                    ))}
                    {dayJobsList.length > 3 && <span className="text-[9px] text-zinc-400">+{dayJobsList.length - 3} more</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* WEEK VIEW */}
      {calView === 'week' && (
        <div className="hidden grid-cols-7 gap-px overflow-hidden rounded-sm border border-zinc-200 bg-zinc-200 md:grid" data-testid="week-calendar">
          {weekDays.map(day => {
            const isToday = isSameDay(day, new Date());
            const dayStr = format(day, 'yyyy-MM-dd');
            const dayJobsList = getJobsForDay(day);
            const isDragOver = dragOverDay === dayStr && dragJobId;
            return (
              <div key={day.toISOString()}
                className={`min-h-[180px] bg-white transition-colors duration-150 ${isToday ? 'bg-zinc-50' : ''} ${isDragOver ? 'bg-blue-50 ring-2 ring-inset ring-blue-300' : ''}`}
                onDragOver={(e) => handleDragOver(e, dayStr)} onDragLeave={handleDragLeave} onDrop={(e) => handleDrop(e, dayStr)}
                data-testid={`cal-day-${dayStr}`}>
                <div className={`px-2 py-1.5 border-b text-center cursor-pointer ${isToday ? 'bg-zinc-950 text-white' : 'bg-zinc-50 hover:bg-zinc-100'}`}
                  onClick={() => goToDay(day)}>
                  <p className="text-[10px] font-bold uppercase tracking-wider">{format(day, 'EEE')}</p>
                  <p className={`text-lg font-bold ${isToday ? '' : 'text-zinc-900'}`}>{format(day, 'd')}</p>
                </div>
                <div className="p-1 space-y-1">
                  {dayJobsList.map(job => (
                    <div key={job.id} draggable onDragStart={(e) => handleDragStart(e, job.id)} onDragEnd={handleDragEnd}
                      className="group cursor-grab rounded-sm border border-transparent p-1.5 text-xs transition-all hover:border-zinc-300 active:cursor-grabbing"
                      style={{ borderLeft: `3px solid ${getDBAColor(job.dba_id)}` }}
                      onClick={() => setShowDetail(job)} data-testid={`cal-job-${job.id}`}>
                      <div className="flex items-start gap-1">
                        <GripVertical className="w-3 h-3 text-zinc-300 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="font-semibold text-zinc-900 truncate">{job.title}</p>
                          {job.scheduled_time_start && <p className="text-zinc-500">{job.scheduled_time_start}</p>}
                          <Badge variant="secondary" className={`text-[9px] mt-0.5 ${statusColors[job.status] || ''}`}>{job.status?.replace('_', ' ')}</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                  {isDragOver && dayJobsList.length === 0 && (
                    <div className="flex h-12 items-center justify-center rounded-sm border-2 border-dashed border-blue-300 text-xs text-blue-600">Drop here</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DAY VIEW */}
      {calView === 'day' && (
        <Card className="rounded-[1.5rem] border border-blue-100 shadow-sm sm:rounded-md sm:border-zinc-200" data-testid="day-view">
          <CardContent className="p-0">
            <div className="divide-y divide-zinc-100">
              {dayTimeSlots.map(slot => {
                const slotJobs = dayJobs.filter(j => j.scheduled_time_start?.slice(0,5) === slot);
                return (
                  <div key={slot} className="flex min-h-[64px]">
                    <div className="w-20 flex-shrink-0 border-r border-blue-50 bg-blue-50 px-3 py-3 text-sm font-black text-blue-700 sm:w-16 sm:bg-zinc-50 sm:text-xs sm:font-medium sm:text-zinc-500">{slot}</div>
                    <div className="flex-1 p-1.5 space-y-1">
                      {slotJobs.map(job => (
                        <div key={job.id} className="flex cursor-pointer flex-col gap-3 rounded-2xl border border-blue-100 bg-white p-3 shadow-sm transition-colors hover:border-blue-200 hover:bg-blue-50 sm:flex-row sm:items-center sm:rounded-sm sm:border-transparent sm:p-2 sm:shadow-none"
                          style={{ borderLeft: `3px solid ${getDBAColor(job.dba_id)}` }}
                          onClick={() => setShowDetail(job)} data-testid={`day-job-${job.id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-lg font-black leading-tight text-zinc-900 sm:truncate sm:text-sm sm:font-semibold">{job.title}</p>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm font-semibold text-zinc-600 sm:text-xs sm:font-normal sm:text-zinc-500">
                              {job.scheduled_time_start && <span>{job.scheduled_time_start}-{job.scheduled_time_end}</span>}
                              {job.address && <span className="truncate"><MapPin className="w-3 h-3 inline mr-0.5" />{job.address}<a href={mapsUrl(job.address)} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()} className="ml-1 inline-flex text-zinc-500 hover:text-zinc-950" data-testid={`job-card-map-link-${job.id}`} aria-label="Open job address in Google Maps"><ExternalLink className="h-3 w-3" /></a></span>}
                            </div>
                          </div>
                          <Badge variant="secondary" className={`text-xs flex-shrink-0 font-black ${statusColors[job.status] || ''}`}>{job.status?.replace('_',' ')}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {/* Unscheduled (no time) */}
              {unscheduledDayJobs.length > 0 && (
                <div className="flex min-h-[48px]">
                  <div className="w-16 flex-shrink-0 py-2 px-3 bg-zinc-50 text-[10px] font-bold uppercase tracking-wider text-zinc-400 border-r border-zinc-100">No time</div>
                  <div className="flex-1 p-1.5 space-y-1">
                    {unscheduledDayJobs.map(job => (
                      <div key={job.id} className="flex cursor-pointer items-center gap-2 rounded-sm border border-transparent p-2 hover:border-zinc-200 hover:bg-zinc-50"
                        style={{ borderLeft: `3px solid ${getDBAColor(job.dba_id)}` }}
                        onClick={() => setShowDetail(job)}>
                        <p className="text-sm font-semibold text-zinc-900 truncate flex-1">{job.title}</p>
                        <Badge variant="secondary" className={`text-xs ${statusColors[job.status] || ''}`}>{job.status?.replace('_',' ')}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Job List */}
      {calView !== 'day' && (
        <Card className="rounded-[1.5rem] border border-blue-100 shadow-sm sm:rounded-md sm:border-zinc-200" data-testid="jobs-list-panel">
          <CardHeader className="pb-3"><CardTitle className="text-2xl font-black font-heading sm:text-base sm:font-bold">{calView === 'month' ? 'This Month Jobs' : 'This Week Jobs'}</CardTitle></CardHeader>
          <CardContent>
            {jobsList.length === 0 ? <p className="text-sm text-zinc-500 text-center py-4">No jobs</p> : (
              <ResponsiveRecordList testId="jobs-responsive-list">
                <div className="flex items-center gap-2 rounded-2xl bg-blue-50 px-3 py-3 sm:bg-transparent sm:py-1">
                  <Checkbox checked={selected.size === jobsList.length && jobsList.length > 0} onCheckedChange={toggleSelectAll} data-testid="select-all-jobs" />
                  <span className="text-sm font-black uppercase tracking-wider text-blue-700 sm:text-xs sm:text-zinc-400">Select all</span>
                </div>
                {jobsList.slice(0, 50).map(job => <ResponsiveRecordCard key={job.id} testId={`job-card-shell-${job.id}`} openTestId={`job-row-${job.id}`} accentColor={getDBAColor(job.dba_id)} title={job.title} subtitle={<><span><CalendarDays className="mr-1 inline h-4 w-4 text-blue-500 sm:h-3 sm:w-3" />{job.scheduled_date}</span>{job.scheduled_time_start && <span> · <Clock className="mr-1 inline h-4 w-4 text-orange-500 sm:h-3 sm:w-3" />{job.scheduled_time_start}</span>}{job.address && <span> · {job.address}</span>}</>} badge={<Badge variant="secondary" className={`shrink-0 text-xs font-black ${statusColors[job.status] || ''}`}>{job.status?.replace('_', ' ')}</Badge>} leading={<Checkbox checked={selected.has(job.id)} onCheckedChange={() => toggleSelect(job.id)} onClick={(e) => e.stopPropagation()} data-testid={`select-job-${job.id}`} />} onOpen={() => setShowDetail(job)} meta={<span>{job.assigned_staff?.length > 0 ? <><Users className="mr-1 inline h-4 w-4 text-blue-500 sm:h-3 sm:w-3" />{job.assigned_staff.map(id => getStaffName(id).split(' ')[0]).join(', ')}</> : 'Unassigned'}</span>} actions={<>{job.late_arrival_alert_sent_at && <Badge variant="secondary" className="bg-red-100 text-red-700 text-xs font-black" data-testid={`job-late-alert-badge-${job.id}`}>Late reviewed</Badge>}{job.eta_status?.arrival_window_label && <Badge variant="secondary" className="bg-orange-100 text-orange-700 text-xs font-black" data-testid={`job-eta-badge-${job.id}`}>ETA {job.eta_status.arrival_window_label}</Badge>}{job.address && <a href={mapsUrl(job.address)} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-12 items-center justify-center gap-1 rounded-2xl bg-indigo-500 px-3 font-black text-white hover:bg-indigo-600 sm:min-h-8 sm:rounded-sm sm:bg-white sm:text-zinc-700 sm:ring-1 sm:ring-zinc-200" data-testid={`job-list-map-link-${job.id}`} aria-label="Open job address in Google Maps"><Navigation className="h-4 w-4" />Map</a>}</>} />)}
              </ResponsiveRecordList>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}
